import React from "react";
import { componentProps } from "components/baseComponent";
import { createValidImageURL } from "data/Utils";
import { Service } from "data/DataTypes";
import {
  faLongArrowAltRight,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/fontawesome-free-solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

const PROJECTS: Service[] = [];

type Props = componentProps & {
  projects?: Service[];
};

type State = {
  current: number;
  firstVisible: number;
};

const GAP_PX = 16;

function GetStartIndex(arr: unknown[]): number {
  return arr.length >= 3 ? 2 : 0;
}

export class ProjectCarousel extends React.Component<Props, State> {
  trackRef = React.createRef<HTMLDivElement>();

  constructor(props: Props) {
    super(props);
    const projects = props.projects ?? PROJECTS;

    console.log("hello world: ", projects.length);

    this.state = {
      current: GetStartIndex(projects),
      firstVisible: 0,
    };
  }

  componentDidMount() {
    window.addEventListener("resize", this.handleResize);

    this.forceUpdate(() => {
      this.goTo(this.state.current);
    });
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize);
  }

  getVisibleCount(): number {
    if (typeof window === "undefined") return 5;
    if (window.innerWidth < 640) return 1;
    if (window.innerWidth < 1024) return 2;
    return 5;
  }

  handleResize = () => this.forceUpdate();

  getCardWidthPct(): number {
    if (typeof window === "undefined") return 20;
    if (window.innerWidth < 640) return 85;
    if (window.innerWidth < 1024) return 50;
    return 20;
  }

  goTo(idx: number) {
    const projects = this.props.projects ?? PROJECTS;
    const visibleCount = this.getVisibleCount();

    this.setState((prev) => {
      let { firstVisible } = prev;

      if (projects.length <= visibleCount) {
        firstVisible = 0;
      } else if (visibleCount === 1) {
        firstVisible = idx;
      } else if (idx <= firstVisible) {
        firstVisible = Math.max(0, idx - 1);
      } else if (idx >= firstVisible + visibleCount - 1) {
        firstVisible = Math.min(
          projects.length - visibleCount,
          idx - visibleCount + 2
        );
      }

      return {
        current: idx,
        firstVisible,
      };
    });
  }

  setCurrent(idx: number) {
    this.setState({ current: idx });
  }

  render() {
    const { current, firstVisible } = this.state;
    const trackW = this.trackRef.current?.getBoundingClientRect().width ?? 0;
    const isMounted = trackW > 0;
    const cardPct = this.getCardWidthPct();
    const cardW = isMounted ? (trackW * cardPct) / 100 : 0;

    const offset = isMounted ? firstVisible * (cardW + GAP_PX) : 0;
    const projects = this.props.projects ?? PROJECTS;
    const visibleProjects =
      projects?.filter((p) => !p.dontShowOnFrontPage) ?? [];

    return (
      <div className="relative w-full max-w-full overflow-hidden mx-auto mt-0">
        <div className="relative w-full mx-auto py-14 xs:max-w-7xl">
          <div ref={this.trackRef} className="relative w-full ml-8 sm:ml-0">
            <div
              className="flex gap-4 transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] mr-auto ml-auto"
              style={{
                width: "max-content",
                transform: isMounted
                  ? `translateX(-${offset}px)`
                  : "translateX(0)",
              }}
            >
              {visibleProjects.map((p, i) => {
                const isActive = i === current;
                const serviceUrl = "/services/" + p.id;

                return (
                  <a
                    key={i}
                    href={serviceUrl}
                    onMouseEnter={() => {
                      if (!isActive) {
                        this.setCurrent(i);
                      }
                    }}
                    style={{
                      width: `${cardPct - 2 + i / 2}vw`,
                      flexShrink: 0,
                      textDecoration: "none",
                      color: "inherit",
                      display: "block",
                    }}
                    className={[
                      "border rounded-xl overflow-hidden transition-all duration-200 select-none cursor-pointer",
                      isActive
                        ? "opacity-100 scale-100 bg-whiteColor border-[#c0c0c0]"
                        : "opacity-100 scale-95 bg-whiteColorDark border-[#e8e8e8]",
                    ].join(" ")}
                  >
                    <div className="flex flex-col h-full">
                      {/* Image */}
                      <div className="relative w-full h-64 flex-shrink-0 bg-[#f0f0ee] overflow-hidden">
                        <img
                          src={createValidImageURL(p.image)}
                          alt={p.imageAlt}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      {/* Text */}
                      <div className="flex flex-col justify-between flex-1 p-6">
                        <div>
                          <p className="text-xs uppercase tracking-widest text-[#888] mb-3">
                            {p.eyebrow}
                          </p>
                          <h3 className="text-xl font-medium text-blackColor leading-snug mb-3">
                            {p.title}
                          </h3>
                          <p className="text-sm text-[#555] leading-relaxed">
                            {p.desc}
                          </p>
                        </div>

                        <div className="flex flex-wrap gap-2 mt-6">
                          {p.meta?.map((m, j) => (
                            <span
                              key={j}
                              className="text-xs px-3 py-1 rounded-full border border-[#e8e8e8] bg-[#f7f7f5] text-[#666]"
                            >
                              {m.icon} {m.label}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Read More */}
                      <div className="p-6 pt-0 flex justify-end">
                        <span
                          className="inline-flex items-center gap-3 px-4 py-2 rounded-lg border border-blackColor
        bg-blackColor text-whiteColor text-lg font-medium"
                        >
                          <span>Read More</span>
                          <FontAwesomeIcon
                            icon={faLongArrowAltRight as IconProp}
                            className="text-xs"
                          />
                        </span>
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>
          </div>

          {/* Nav: arrows + dots */}
          <div className="flex items-center justify-center gap-4 mt-12 px-4">
            <button
              onClick={() => this.goTo(Math.max(0, current - 1))}
              disabled={current === 0}
              aria-label="Previous project"
              className="inline-flex items-center justify-center w-10 h-10 rounded-lg border border-[#e8e8e8] bg-blackColor
               text-whiteColor transition-all duration-200 hover:border-[#d8d8d8] hover:-translate-y-0.5 disabled:opacity-30 
               disabled:cursor-not-allowed disabled:hover:translate-y-0 mr-12"
            >
              <FontAwesomeIcon
                icon={faChevronLeft as IconProp}
                className="text-sm"
              />
            </button>

            <div className="flex flex-wrap gap-1.5">
              {visibleProjects.map((_, i) => (
                <button
                  key={i}
                  onClick={() => this.goTo(i)}
                  aria-label={"Go to project " + (i + 1)}
                  className={[
                    "w-1.5 h-1.5 rounded-full transition-all duration-200",
                    i === current ? "bg-blackColor scale-[1.3]" : "bg-[#ddd]",
                  ].join(" ")}
                />
              ))}
            </div>

            <button
              className="inline-flex items-center justify-center w-10 h-10 rounded-lg border border-[#e8e8e8] bg-blackColor 
              text-whiteColor transition-all duration-200 hover:border-[#d8d8d8] hover:-translate-y-0.5 
              disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:translate-y-0 ml-12"
              onClick={() =>
                this.goTo(Math.min(visibleProjects.length - 1, current + 1))
              }
              disabled={current === visibleProjects.length - 1}
              aria-label="Next project"
            >
              <FontAwesomeIcon
                icon={faChevronRight as IconProp}
                className="text-sm"
              />
            </button>
          </div>
        </div>
      </div>
    );
  }
}
